<template>
    <AppModal ref="Modal" :title="$t('labels.adding_filter')" size="lg" @onHide="hide">
        <div class="modal-body">
            <FormGroup :label="$t('labels.filter')">
                <AppAutoComplete
                    :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filters`"
                    :model-value="filter?.uuid ?? ''"
                    :selected-value="getFilterName(filter) ?? ''"
                    :selected-values="availableFilterUuids"
                    @update:model-value="(newFilter) => {filter = newFilter; filterItem = null}"
                    :query-params="{
                        integration_category_uuids: [category.uuid]
                    }"
                >
                    <template #optionItem="{ option }">
                        {{ getFilterName(option) }}
                    </template>
                </AppAutoComplete>
            </FormGroup>
            <div class="row mb-2">
                <div class="col-6">
                    <InputGroup>
                        <Radio
                            :model-value="filterType === 'not_variable'"
                            @update:model-value="filterType = 'not_variable'"
                        />
                        <template #append>
                            {{ $t('labels.not_variable_filter') }}
                        </template>
                    </InputGroup>
                </div>
                <div class="col-6">
                    <InputGroup>
                        <Radio
                            :model-value="filterType === 'variable'"
                            @update:model-value="filterType = 'variable'"
                        />
                        <template #append>
                            {{ $t('labels.variable_filter') }}
                        </template>
                    </InputGroup>
                </div>
            </div>
            <slot v-if="filterType === 'not_variable'">
                <FormGroup :label="$t('labels.filter_item')">
                    <CatalogAttributeSelect
                        v-if="filter"
                        :attribute="filter"
                        :integration="integration"
                        :category="category"
                        :model-value="filterItems"
                        @update:model-value="filterItems = $event"
                    />
                    <input class="form-control" v-else disabled :placeholder="$t('labels.empty')" />
                </FormGroup>
            </slot>
        </div>
        <slot v-if="filterType === 'variable'">
            <div class="table-responsive">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="500px">{{ $t('labels.product') }}</th>
                            <th>
                                <AppNullableCell>
                                    <template #default v-if="filter">
                                        {{ filter.name }}
                                    </template>
                                </AppNullableCell>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="product in products">
                            <td>
                                <div class="d-flex product-cell">
                                    <img 
                                        class="table-image mr-2" 
                                        :src="product.picture_url ?? PLACEHOLDER_IMAGE_URL" 
                                        :alt="product.name"
                                    />
                                    <div class="text-container">
                                        <span class="d-block text-nowrap">
                                            {{ product.name }}
                                        </span>
                                        <span class="text-muted text-smaller d-block">
                                            {{ product.sku }}
                                        </span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <CatalogAttributeSelect
                                    v-if="filter"
                                    :attribute="filter"
                                    :integration="integration"
                                    :category="category"
                                    :model-value="product.filters[filter.uuid] || []"
                                    @update:model-value="product.filters[filter.uuid] = $event"
                                />
                                <input class="form-control" v-else disabled :placeholder="$t('labels.empty')" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </slot>
        <div class="modal-footer">
            <AppButton size="sm" color="indigo" @click="saveFilter" :disabled="!canByAdded">
                {{ $t('buttons.add') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import AppAutoComplete from '../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import Radio from '../../../../components/new/form/radio/Radio.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import AppNullableCell from '../../../components/tables/AppNullableCell.vue';
import CatalogAttributeSelect from '../sidebarCards/products/edit/CatalogAttributeSelect.vue';

import { httpClient } from '../../../../services/HttpClient';
import { chunk } from '../../../../utils';

export default {
    name: 'AddIntegrationGroupFilterModal',
    components: {
        AppModal, AppAutoComplete, Radio, FormGroup, InputGroup, AppButton, AppNullableCell,
        CatalogAttributeSelect
    },
    props: {
        category: {
            type: Object,
            required: true
        },
        integration: {
            type: Object,
            required: true
        },
        availableFilterUuids: {
            type: Array,
            default: () => []
        },
        getFilterName: {
            type: Function,
            required: true
        }
    },
    emits: [
        'filterAdded'
    ],
    data() {
        return {
            products: [],
            productsString: '',
            filter: null,
            filterItems: [],
            filterType: 'not_variable',
        }
    },
    methods: {
        show(products) {
            this.productsString = JSON.stringify(products);
            this.products = products

            this.$refs.Modal.show(products);
        },
        hide() {
            this.$refs.Modal.hide();
            this.filter = null;
            this.filterItems = [];
            this.filterType = 'not_variable';
            this.products = [];
        },
        async saveFilter() {
            if (this.filterType === 'variable') {
                let requestData = this.products.map(product => ({
                    method: 'PATCH',
                    path: `/integrations/products/${product.uuid}`,
                    body: {
                        filter_item_uuids: Object.values(product.filters).flat().map((filterItem) => filterItem.uuid),
                    }
                }))

                await Promise.all(chunk(requestData, 10).map(async (chunk) => {
                    await httpClient.post('/batch', {
                        data: chunk
                    });
                }));

                this.$emit('filterAdded');
                this.hide();
                return;
            }

            if (this.filterType === 'not_variable') {
                this.products.forEach(product => {
                    product.filters[this.filter.uuid] = this.filterItems;
                });

                let requestData = this.products.map(product => ({
                    method: 'PATCH',
                    path: `/integrations/products/${product.uuid}`,
                    body: {
                        filter_item_uuids: Object.values(product.filters).flat().map((filterItem) => filterItem.uuid)
                    }
                }))

                await Promise.all(chunk(requestData, 10).map(async (chunk) => {
                    await httpClient.post('/batch', {
                        data: chunk
                    });
                }));

                this.$emit('filterAdded');
                this.hide();
                return;
            }
        }
    },
    computed: {
        canByAdded() {
            if (this.filterType === 'variable') {
                return this.products.some(product => {
                    return product.filters[this.filter?.uuid] !== undefined
                })
            };

            if (this.filterType === 'not_variable') {
                return this.filterItems.length > 0;
            };

            return false
        }
    },
    watch: {
        filter(newFilter, oldFilter) {
            if (oldFilter && newFilter && newFilter.uuid !== oldFilter.uuid) {
                this.filterItems = [];
                this.products = JSON.parse(this.productsString);
            }
        }
    }
}
</script>
<style scoped>
.table-image {
    width: 38px;
    height: 38px;
}
.text-nowrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.text-container {
    min-width: 0;
    overflow: hidden;
    flex: 1;
}
.product-cell {
    max-width: 100%;
    width: 100%;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
</style>